import React, { useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";

/**
 * Phase 12.3 — Compact "coverage mix" donut.
 *
 * Renders a per-life-stage recommended protection allocation. Percentages are
 * illustrative, not a recommendation for any individual client. Same NAIC
 * disclaimer language applies — kept inside the Journey card where this is
 * embedded.
 */

const STAGE_MIX = {
  childhood: [
    { name: "Education savings", value: 45, color: "#2E7D32" },
    { name: "Family life insurance", value: 30, color: "#D4AF37" },
    { name: "Emergency fund", value: 15, color: "#0A2540" },
    { name: "Health coverage", value: 10, color: "#1F3B5C" },
  ],
  "young-adult": [
    { name: "Term life insurance", value: 30, color: "#D4AF37" },
    { name: "Disability income", value: 25, color: "#2E7D32" },
    { name: "Emergency fund", value: 20, color: "#0A2540" },
    { name: "Retirement starter", value: 15, color: "#1F3B5C" },
    { name: "Health coverage", value: 10, color: "#5A6472" },
  ],
  "mid-life": [
    { name: "Term life insurance", value: 40, color: "#D4AF37" },
    { name: "Disability income", value: 20, color: "#2E7D32" },
    { name: "College savings", value: 15, color: "#0A2540" },
    { name: "Retirement accumulation", value: 15, color: "#1F3B5C" },
    { name: "Emergency fund", value: 10, color: "#5A6472" },
  ],
  "pre-retirement": [
    { name: "Permanent / IUL", value: 30, color: "#D4AF37" },
    { name: "Annuity accumulation", value: 25, color: "#2E7D32" },
    { name: "Long-term care", value: 20, color: "#0A2540" },
    { name: "Disability income", value: 15, color: "#1F3B5C" },
    { name: "Medicare prep", value: 10, color: "#5A6472" },
  ],
  retirement: [
    { name: "Income annuity", value: 35, color: "#D4AF37" },
    { name: "Long-term care", value: 25, color: "#0A2540" },
    { name: "Medicare supplement", value: 20, color: "#1F3B5C" },
    { name: "Estate / legacy", value: 15, color: "#2E7D32" },
    { name: "Liquidity reserve", value: 5, color: "#5A6472" },
  ],
};

export const LifeStageMixDonut = ({ stageId }) => {
  const data = useMemo(() => STAGE_MIX[stageId] || STAGE_MIX["mid-life"], [stageId]);

  return (
    <div data-testid={`stage-mix-donut-${stageId}`} className="w-full">
      <div className="font-sans-ui text-[0.72rem] tracking-[0.22em] uppercase text-lw-gold mb-3">
        Illustrative coverage mix
      </div>
      <div className="flex items-center gap-5 flex-wrap">
        <div className="w-[140px] h-[140px] flex-shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                innerRadius={40}
                outerRadius={64}
                paddingAngle={2}
                dataKey="value"
                stroke="rgba(10,37,64,0.3)"
                strokeWidth={1}
                isAnimationActive
                animationDuration={700}
              >
                {data.map((d, i) => (
                  <Cell key={i} fill={d.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
        <ul className="flex-1 min-w-[160px] space-y-1.5">
          {data.map((d) => (
            <li
              key={d.name}
              className="flex items-center justify-between gap-3 text-xs text-[#FAF7F2]/90"
            >
              <span className="flex items-center gap-2 min-w-0">
                <span
                  className="h-2 w-2 rounded-full flex-shrink-0"
                  style={{ background: d.color }}
                />
                <span className="truncate">{d.name}</span>
              </span>
              <span className="font-semibold tabular-nums text-lw-gold">
                {d.value}%
              </span>
            </li>
          ))}
        </ul>
      </div>
      <p className="mt-3 text-[0.65rem] leading-snug text-[#FAF7F2]/55">
        Illustrative allocations. Not a specific recommendation. Suitable mix
        varies by income, dependents, state, and underwriting.
      </p>
    </div>
  );
};

export default LifeStageMixDonut;
